/**
 * Sub-tab "Mayores Variaciones" — top N cuentas con mayor variación absoluta.
 * Layout: full-width desktop. Tabla con sticky header, búsqueda y scroll.
 */
import React, { useEffect, useState } from 'react';
import { Select, Card } from '../../../../../shared/components';
import { ComparativosMultiSelect } from './ComparativosMultiSelect';
import { getComparativosRepository } from '../../../../../shared/services/results/comparativosRepository';
import { getResultsRepository } from '../../../../../shared/services/results/repositoryFactory';
import { TopVariationItemDto } from '../../../../../shared/services/results/types.comparativos';
import { ServiceType } from '../../../../../shared/services/results/types';
import { TopVariationsChart } from './TopVariationsChart';
import { money, pctFmt, varColor, yearOptions, monthOptions, MONTH_LABELS } from './comparativosUtils';
import { exportTopVariationsExcel } from '../../../utils/exportComparativos';

const repo = getComparativosRepository();
const baseRepo = getResultsRepository();

type Mode = 'year' | 'month' | 'accumulated';
const MODE_LABELS: Record<Mode, string> = { year: 'Año vs Año', month: 'Mes vs Mes', accumulated: 'Acumulado' };

export function TopVariationsTab() {
  const now = new Date();
  const currentMonthKey = [
    'enero','febrero','marzo','abril','mayo','junio',
    'julio','agosto','septiembre','octubre','noviembre','diciembre',
  ][now.getMonth()];

  const [mode, setMode] = useState<Mode>('year');
  const [anio1, setAnio1] = useState(now.getFullYear() - 1);
  const [anio2, setAnio2] = useState(now.getFullYear());
  const [mes1, setMes1] = useState('septiembre');
  const [mes2, setMes2] = useState('septiembre');
  const [mesCorte, setMesCorte] = useState(currentMonthKey);
  const [topN, setTopN] = useState(10);
  const [tiposServicio, setTiposServicio] = useState<string[]>([]);
  const [clientes, setClientes] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [allClients, setAllClients] = useState<string[]>([]);
  const [data, setData] = useState<TopVariationItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([baseRepo.listServiceTypes(), baseRepo.listDistinctClients()]).then(([st, cl]: [ServiceType[], string[]]) => {
      setServiceTypes(st);
      setAllClients(cl);
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    repo
      .getTopVariations({
        mode, anio1, anio2,
        mes1: mode === 'month' ? mes1 : undefined,
        mes2: mode === 'month' ? mes2 : undefined,
        mesCorte: mode === 'accumulated' ? mesCorte : undefined,
        topN,
        tiposServicio: tiposServicio.length ? tiposServicio : undefined,
        clientes: clientes.length ? clientes : undefined,
      })
      .then((d: TopVariationItemDto[]) => { setData(d); setIsLoading(false); })
      .catch((err) => { console.error('[TopVariationsTab]', err); setIsLoading(false); });
  }, [mode, anio1, anio2, mes1, mes2, mesCorte, topN, tiposServicio, clientes]);

  const yOpts = yearOptions(now.getFullYear());

  const buildLabels = (): [string, string] => {
    if (mode === 'year') return [String(anio1), String(anio2)];
    if (mode === 'month') return [`${MONTH_LABELS[mes1] ?? mes1} ${anio1}`, `${MONTH_LABELS[mes2] ?? mes2} ${anio2}`];
    const ml = MONTH_LABELS[mesCorte] ?? mesCorte;
    return [`Ene–${ml} ${anio1}`, `Ene–${ml} ${anio2}`];
  };
  const [label1, label2] = buildLabels();

  const q = search.trim().toLowerCase();
  const displayed = q
    ? data.filter((it) => it.cuentaCodigo.toLowerCase().includes(q) || it.cuentaDescripcion.toLowerCase().includes(q))
    : data;

  return (
    <div className="relative left-1/2 -ml-[50vw] w-screen px-4 sm:px-6 lg:px-8">
      <div className="space-y-4">
        {/* Selector de modo */}
        <div className="flex gap-2">
          {(Object.keys(MODE_LABELS) as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${mode === m ? 'bg-brand-600 text-white' : 'bg-surface-2 text-ink-600 hover:bg-surface-3'}`}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>

        {/* Filtros según modo */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 lg:max-w-5xl">
          {mode === 'year' && (
            <>
              <Select label="Año 1" options={yOpts} value={String(anio1)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio1(Number(e.target.value))} />
              <Select label="Año 2" options={yOpts} value={String(anio2)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio2(Number(e.target.value))} />
            </>
          )}
          {mode === 'month' && (
            <>
              <Select label="Mes 1" options={monthOptions} value={mes1} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMes1(e.target.value)} />
              <Select label="Año 1" options={yOpts} value={String(anio1)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio1(Number(e.target.value))} />
              <Select label="Mes 2" options={monthOptions} value={mes2} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMes2(e.target.value)} />
              <Select label="Año 2" options={yOpts} value={String(anio2)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio2(Number(e.target.value))} />
            </>
          )}
          {mode === 'accumulated' && (
            <>
              <Select label="Mes de corte" options={monthOptions} value={mesCorte} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMesCorte(e.target.value)} />
              <Select label="Año 1" options={yOpts} value={String(anio1)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio1(Number(e.target.value))} />
              <Select label="Año 2" options={yOpts} value={String(anio2)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAnio2(Number(e.target.value))} />
            </>
          )}
          <Select
            label="Top N"
            options={[5, 10, 15, 20].map((n) => ({ value: String(n), label: `Top ${n}` }))}
            value={String(topN)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTopN(Number(e.target.value))}
          />
          <ComparativosMultiSelect label="Tipo Servicio" options={serviceTypes.map((s) => s.nombre)} selected={tiposServicio} onApply={setTiposServicio} />
          <ComparativosMultiSelect label="Cliente" options={allClients} selected={clientes} onApply={setClientes} />
        </div>

        {/* Excel */}
        {data.length > 0 && (
          <div className="flex justify-end">
            <button
              onClick={() => exportTopVariationsExcel(data, label1, label2)}
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-95"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Descargar Excel
            </button>
          </div>
        )}

        {isLoading && <p className="py-8 text-center text-sm text-ink-400">Cargando variaciones...</p>}

        {!isLoading && data.length > 0 && (
          <>
            <Card>
              <TopVariationsChart items={data} label1={label1} label2={label2} />
            </Card>

            {/* Búsqueda */}
            <div className="flex justify-end">
              <div className="relative">
                <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Buscar cuenta..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm text-ink-700 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700">✕</button>
                )}
              </div>
            </div>

            {/* Tabla con paneles fijos */}
            <div className="overflow-auto rounded-xl border border-border" style={{ maxHeight: 'calc(100vh - 380px)' }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-2 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3">#</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3">Tipo</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3">Cuenta</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">{label1}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">{label2}</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Variación $</th>
                    <th className="sticky top-0 z-20 whitespace-nowrap border-b border-ink-100 bg-surface-2 px-4 py-3 text-right">Variación %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayed.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-sm text-ink-400">
                        {search ? `Sin resultados para "${search}"` : 'No hay datos.'}
                      </td>
                    </tr>
                  )}
                  {displayed.map((it, idx) => {
                    const colV = varColor(it.variacionAbsoluta, it.tipoCuenta);
                    const isIngFav = it.tipoCuenta === 'INGRESO' && it.variacionAbsoluta > 0;
                    const isGtoFav = it.tipoCuenta === 'GASTOS' && it.variacionAbsoluta < 0;
                    const favorable = isIngFav || isGtoFav;
                    return (
                      <tr key={`${it.tipoCuenta}::${it.cuentaCodigo}`} className="transition-colors hover:bg-surface-2">
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs font-bold text-ink-400">#{idx + 1}</td>
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${it.tipoCuenta === 'INGRESO' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                            {it.tipoCuenta === 'INGRESO' ? 'ING' : 'GTO'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5">
                          <span className="font-mono text-xs text-ink-400">{it.cuentaCodigo}</span>
                          <span className="ml-2 text-ink-700">{it.cuentaDescripcion}</span>
                          <span className={`ml-2 text-[10px] font-bold ${favorable ? 'text-emerald-600' : 'text-red-600'}`}>
                            {favorable ? '▲ FAV' : '▼ DESF'}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-ink-600">{money(it.valor1)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono font-semibold text-ink-900">{money(it.valor2)}</td>
                        <td className={`whitespace-nowrap px-4 py-2.5 text-right font-mono font-bold ${colV}`}>
                          {it.variacionAbsoluta >= 0 ? '+' : ''}{money(it.variacionAbsoluta)}
                        </td>
                        <td className={`whitespace-nowrap px-4 py-2.5 text-right font-semibold ${colV}`}>
                          {pctFmt(it.variacionPorcentaje)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {displayed.length !== data.length && (
              <p className="text-xs text-ink-400">Mostrando {displayed.length} de {data.length} cuentas.</p>
            )}
          </>
        )}

        {!isLoading && data.length === 0 && (
          <p className="py-8 text-center text-sm text-ink-400">No hay datos para los filtros seleccionados.</p>
        )}
      </div>
    </div>
  );
}
